import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AuthImg } from "@/components/AuthImg";
import { Badge } from "@/components/ui/badge";
import { Package, X } from "lucide-react";

// CHECK A HIGH SHELF FROM THE GROUND. His words. Parts stand on the SHELF, not on the rack, so a
// rack's own row shows nothing without reading the whole subtree. This opens INLINE, right under
// the row he is already looking at — he never leaves the list and never loses his place.
export const RackContents = ({ node, depth = 0, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null); setErr(null);
    api.get(`/locations/${node.id}/contents`, { params: { deep: 1 } })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't read it."); });
    return () => { alive = false; };
  }, [node.id]);

  return (
    <div data-testid={`rack-contents-${node.short_code}`} className="border-t border-slate-100 bg-slate-50/70">
      <div className="flex items-center gap-2 px-3 py-2" style={{ paddingLeft: `${12 + depth * 22}px` }}>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
          What's on {node.short_code}
        </span>
        {data && (
          <span data-testid={`rack-contents-total-${node.short_code}`} className="font-mono text-[10px] text-slate-400">
            {data.deep_part_count} part{data.deep_part_count === 1 ? "" : "s"} · {(data.deep_places || []).length} {(data.deep_places || []).length === 1 ? "shelf" : "shelves"}
          </span>
        )}
        <button data-testid={`rack-contents-close-${node.short_code}`} onClick={onClose}
          className="ml-auto text-slate-400 hover:text-slate-700 p-1" title="Close">
          <X className="w-4 h-4" />
        </button>
      </div>
      {err ? <div className="px-4 pb-3 font-mono text-xs text-[#B01E28]">{err}</div>
        : !data ? <div className="px-4 pb-3 font-mono text-xs text-slate-400">LOADING…</div>
        : (data.deep_places || []).length === 0 && data.part_count === 0
          ? <div data-testid={`rack-contents-empty-${node.short_code}`} className="px-4 pb-3 font-mono text-xs text-slate-400">
              NO SHELVES ON IT YET — use Add shelves on its row
            </div>
          : (
            <div className="pb-2" style={{ paddingLeft: `${12 + depth * 22}px` }}>
              {(data.deep_places || []).map((s) => (
                <div key={s.id} data-testid={`rack-shelf-${s.short_code}`} className="px-1 py-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-slate-700">{s.name}</span>
                    <Badge className="rounded-lg bg-white border border-slate-200 text-slate-600 text-[9px] font-mono">{s.short_code}</Badge>
                    <span className={`font-mono text-[10px] ${s.part_count ? "text-[#C76714]" : "text-slate-400"}`}>
                      {s.part_count ? `${s.part_count} part${s.part_count === 1 ? "" : "s"}` : "empty"}
                    </span>
                  </div>
                  {s.parts.length > 0 && (
                    <div className="mt-1 ml-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {s.parts.map((p) => (
                        <button key={p.id} data-testid={`rack-part-${p.short_code}`} onClick={() => navigate(`/parts/${p.id}`)}
                          className="min-h-[48px] flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left hover:border-[#E0781A] transition-colors">
                          <span className="w-9 h-9 rounded-md bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                            {p.photo ? <AuthImg fileId={p.photo} thumb className="w-full h-full object-cover" alt={p.short_code} />
                              : <Package className="w-4 h-4 text-slate-300" />}
                          </span>
                          <span className="font-mono text-[11px] shrink-0">{p.short_code}</span>
                          <span className="flex-1 truncate text-sm">{p.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
    </div>
  );
};
